import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

export interface AppNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  body: string;
  payload: Record<string, unknown>;
  deep_link: string | null;
  priority: number;
  created: string;
  read_at: string | null;
  expires_at: string | null;
}

export interface NotificationFeedResponse {
  items: AppNotification[];
  next_cursor: string | null;
}

/** Cognito-aware token lookup. Delegates to the canonical {@link authHeader} (feat-026). */
function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

function backendUrl(path: string): string {
  const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
};

async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
  };
  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.body !== undefined) {
    init.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }
  const resp = await authedApiFetch(backendUrl(path), init);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function fetchManagerFeed(opts: {
  limit?: number;
  cursor?: string;
  unreadOnly?: boolean;
  category?: string;
}): Promise<NotificationFeedResponse> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  if (opts.unreadOnly) params.set('unread_only', 'true');
  if (opts.category) params.set('category', opts.category);
  const qs = params.toString();
  return api<NotificationFeedResponse>(`/api/management/notifications${qs ? `?${qs}` : ''}`);
}

export async function fetchManagerUnreadCount(): Promise<number> {
  const r = await api<{ count: number }>('/api/management/notifications/unread-count');
  return r.count ?? 0;
}

export async function markRead(notificationId: string): Promise<void> {
  await api(`/api/management/notifications/${notificationId}/read`, { method: 'POST' });
}

export async function markAllRead(): Promise<void> {
  await api('/api/management/notifications/read-all', { method: 'POST' });
}

export async function dismissNotification(notificationId: string): Promise<void> {
  await api(`/api/management/notifications/${notificationId}/dismiss`, { method: 'POST' });
}

export async function fetchServerNotifications(serverId: string, opts: { limit?: number; cursor?: string } = {}): Promise<NotificationFeedResponse> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.cursor) params.set('cursor', opts.cursor);
  const qs = params.toString();
  return api<NotificationFeedResponse>(`/api/management/servers/${serverId}/notifications${qs ? `?${qs}` : ''}`);
}

// ----- Admin composer -----

export interface AudienceSpec {
  kind: 'server' | 'managers_at_location' | 'managers_for_shift' | 'corporate_managers' | 'explicit' | 'multiple';
  [k: string]: unknown;
}

export interface ComposerRequest {
  audience: AudienceSpec;
  variant: 'celebration' | 'manager' | 'coaching' | 'alert' | 'system' | 'shift' | 'progress';
  title: string;
  body: string;
  deep_link?: string;
  fire_at?: string; // ISO datetime
  tenant_id?: string; // required for admins without a management row
}

export interface ComposerResponse {
  scheduled: boolean;
  notification_id?: string;
  scheduled_id?: string;
}

export async function sendAdminNotification(req: ComposerRequest): Promise<ComposerResponse> {
  return api<ComposerResponse>('/api/admin/notifications', {
    method: 'POST',
    body: req,
  });
}

export interface AudiencePreview {
  total: number;
  by_locale: Record<string, number>;
}

export async function previewAudience(audience: AudienceSpec, tenantId?: string): Promise<AudiencePreview> {
  return api<AudiencePreview>('/api/admin/notifications/preview-audience', {
    method: 'POST',
    body: { audience, tenant_id: tenantId },
  });
}
